import { useState } from "react";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { diffSummary, permissionDiff } from "@/pages/capability-diff";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";

export interface CapabilityDrawerItem {
  name: string;
  detail?: string;
  permissions: string[];
  /**
   * The permissions this item replaces, when it is an upgrade. Present only when a comparison is
   * possible: an install from nothing has no previous list, and a diff against an empty set is
   * just the permissions section again.
   */
  previousPermissions?: string[];
  /**
   * True when no reviewed permission list is available for the capability. The drawer then
   * shows an explicit unknown state instead of an empty list.
   */
  permissionsUnknown?: boolean;
  version?: string;
  source?: string;
  state?: {
    label: string;
    variant: "success" | "warning" | "danger" | "info" | "muted" | "unknown";
  };
  workflows: string[];
}

export function CapabilityDrawer({
  item,
  kind,
  onClose,
  onRemove,
}: {
  item: CapabilityDrawerItem | null;
  kind: "skill" | "tool";
  onClose: () => void;
  onRemove: (item: CapabilityDrawerItem) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = item !== null;
  const used = (item?.workflows.length ?? 0) > 0;
  const kindLabel = kind === "skill" ? "Skill" : "Tool";

  const close = () => {
    setConfirming(false);
    setRemoving(false);
    setError(null);
    onClose();
  };

  const remove = async () => {
    if (!item) return;
    setRemoving(true);
    setError(null);
    try {
      await onRemove(item);
      close();
    } catch {
      setError(`The ${kind} could not be removed. Try again.`);
      setRemoving(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <SheetContent side="right" aria-label={`${kindLabel} details`}>
        {item && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <SheetTitle>{item.name}</SheetTitle>
              <SheetDescription>
                {kindLabel} details: permissions, version, workflows using it, and removal.
              </SheetDescription>
              {item.detail && (
                <p className="font-mono text-xs text-muted-foreground">{item.detail}</p>
              )}
              {item.state && <Badge variant={item.state.variant}>{item.state.label}</Badge>}
            </div>

            <section
              aria-labelledby="capability-permissions-heading"
              className="flex flex-col gap-2"
            >
              <SectionHeading id="capability-permissions-heading">Permissions</SectionHeading>
              {(() => {
                // A comparison is only shown where one exists: the user is deciding whether this
                // version hands over authority the project does not have yet, and a bare list
                // buries the two that are new among the ones already granted.
                const diff = item.previousPermissions
                  ? permissionDiff(item.previousPermissions, item.permissions)
                  : null;
                const summary = diff ? diffSummary(diff) : null;
                if (!diff) return null;
                return (
                  <div className="flex flex-col gap-2 rounded-md border bg-muted/40 px-3 py-2">
                    <p className="text-sm font-medium">
                      {summary ?? "This asks for no permission the project does not already have."}
                    </p>
                    {diff.added.length > 0 ? (
                      <ul className="flex flex-col gap-1 text-sm">
                        {diff.added.map((permission) => (
                          <li key={permission}>
                            <Badge variant="warning">New</Badge>{" "}
                            <span className="font-mono text-xs">{permission}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {diff.removed.length > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No longer asked for: {diff.removed.join(", ")}.
                      </p>
                    ) : null}
                  </div>
                );
              })()}
              {item.permissionsUnknown ? (
                <p className="text-sm text-muted-foreground">
                  Permissions not declared: no reviewed permission list is available, so none is
                  shown here.
                </p>
              ) : item.permissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No permissions recorded.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {item.permissions.map((permission) => (
                    <li key={permission} className="rounded-md border bg-card px-3 py-2">
                      {permission}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="capability-version-heading" className="flex flex-col gap-2">
              <SectionHeading id="capability-version-heading">Version</SectionHeading>
              <dl className="grid gap-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Version</dt>
                  <dd className="font-mono">{item.version ?? "Not recorded"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Comes from</dt>
                  <dd>{item.source ?? "Not recorded"}</dd>
                </div>
              </dl>
            </section>

            <section aria-labelledby="capability-usage-heading" className="flex flex-col gap-2">
              <SectionHeading id="capability-usage-heading">Workflows using it</SectionHeading>
              {item.workflows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No workflows use this yet.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {item.workflows.map((workflow) => (
                    <li key={workflow} className="rounded-md border bg-card px-3 py-2">
                      {workflow}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="capability-remove-heading" className="flex flex-col gap-2">
              <SectionHeading id="capability-remove-heading">Remove</SectionHeading>
              {used && !confirming && (
                <p
                  role="alert"
                  className="rounded-md border border-warning/40 bg-warning/6 px-3 py-2 text-sm text-warning"
                >
                  This {kind} is used by {item.workflows.length} workflow
                  {item.workflows.length === 1 ? "" : "s"}. Removing it lists the affected workflows
                  below so an approver can update them first.
                </p>
              )}
              {confirming ? (
                <>
                  {used && (
                    <div className="rounded-md border border-danger/35 bg-danger/6 px-3 py-2 text-sm">
                      <p className="font-medium text-danger">
                        Removing this {kind} affects {item.workflows.length} workflow
                        {item.workflows.length === 1 ? "" : "s"}:
                      </p>
                      <ul className="mt-1 flex flex-col gap-1 text-danger">
                        {item.workflows.map((workflow) => (
                          <li key={workflow}>{workflow}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {error && (
                    <p role="alert" className="text-sm text-danger">
                      {error}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="destructive" disabled={removing} onClick={() => void remove()}>
                      {removing ? "Removing…" : `Remove ${kind}`}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={removing}
                      onClick={() => setConfirming(false)}
                    >
                      Keep
                    </Button>
                  </div>
                </>
              ) : (
                <Button variant="destructive" onClick={() => setConfirming(true)}>
                  Remove {kind}
                </Button>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
